import {ChangeDetectionStrategy, Component, effect, ElementRef, Inject, ViewChild} from '@angular/core';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {WINDOW} from '../../../universal/window/window.service';
import {RtElementResizedDirective} from '../../../rt-element-resized/rt-element-resized.directive';
import {DashboardStudyViewerControllerService} from '../../../dashboard-study/services/dashboard-study-viewer-controller/dashboard-study-viewer-controller.service';
import {calculateBestFit} from '../../../shared/utils/calculate-best-fit.utils';
import {DashboardStudyViewerLoaderComponent} from '../../../dashboard-study/components/dashboard-study-viewer-loader/dashboard-study-viewer-loader.component';
import {DashboardStudyViewerLoadingState} from '../../../dashboard-study/symbols/dashboard-study-viewer.symbols';
import {DashboardStudyViewerMode} from '../../../dashboard-study/symbols';
import {DashboardStudyViewerZoomedControlsComponent} from '../../../dashboard-study-viewer-zoomed-controls/components/dashboard-study-viewer-zoomed-controls/dashboard-study-viewer-zoomed-controls.component';
import {DashboardStudyViewerControlsComponent} from '../../../dashboard-study/components/dashboard-study-viewer-controls/dashboard-study-viewer-controls.component';

@Component({
  selector: 'app-dashboard-study-papaya',
  templateUrl: './dashboard-study-papaya.component.html',
  styleUrls: ['./dashboard-study-papaya.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    RtElementResizedDirective,
    DashboardStudyViewerLoaderComponent,
    NgOptimizedImage,
    DashboardStudyViewerZoomedControlsComponent,
    DashboardStudyViewerControlsComponent,
  ],
})
export class DashboardStudyPapayaComponent {
  @ViewChild('contentFitter') contentFitter: ElementRef<HTMLDivElement>;
  @ViewChild('content') content: ElementRef<HTMLDivElement>;

  /** Enum representing the loading states of the study viewer. */
  public readonly DashboardStudyViewerLoadingState = DashboardStudyViewerLoadingState;
  public readonly viewerState = this.studyViewerController.currentLoadingStatus;
  public readonly studyViewerMode = this.studyViewerController.studyViewerMode;
  public readonly isZoomedView = this.studyViewerController.isZoomedView;
  public readonly isPapayaRendered = this.studyViewerController.isPapayaRendered;

  /**
   * Size (px) of the other elements in the study viewer.
   * 57px - header
   * 111px - carousel
   **/
  public readonly upperElementsHeight = 168;

  /** Enum representing the available study viewer modes for the dashboard. */
  protected readonly dashboardStudyViewerMode = DashboardStudyViewerMode;

  constructor(@Inject(WINDOW) private window: Window, private studyViewerController: DashboardStudyViewerControllerService) {
    // Update the papaya content size when the view mode changes.
    effect(() => {
      this.studyViewerController.isPapayaOrthogonalView(); // Trigger the effect when the orthogonal changes.
      this.updateContentSize();
    });
  }

  /**
   * Updates the size of the papaya content based on the available space.
   */
  public updateContentSize() {
    // If the contentFitter is not available, we cannot update the size.
    if (!this.contentFitter) {
      return;
    }

    // Single papaya slice is square.
    // So, for any surface mode it's ratio is always 1:1.
    // For any scan mode it's always three slices on the screen, changing the ratio to 3 (width) : 2 (height).
    // We need to first check the current mode of the study viewer to apply the correct size.
    const isPapayaOrthogonalView = this.studyViewerController.isPapayaOrthogonalView();

    // For surface mode, the maximum acceptable width is just all free height.
    // But for the scan mode the viewer is wider, so it can fit even on the smaller height.
    // Define the height to width ratio based on the current mode.
    const heightToWidthRatio = isPapayaOrthogonalView ? 3 / 2 : 1;

    // Get the necessary measurements.
    const windowHeight = this.window.innerHeight;
    const availableWidth = this.contentFitter.nativeElement.getBoundingClientRect().width;

    // In the zoomed view, we do not reserve space for the upper elements as they are not visible.
    const reservedHeight = this.isZoomedView() ? 0 : this.upperElementsHeight;
    const availableHeight = windowHeight - reservedHeight;

    // Calculate the maximum possible size of the papaya content.
    const sizesToApply = calculateBestFit(availableWidth, availableHeight, heightToWidthRatio);

    // Update the content element styles.
    this.content.nativeElement.style.width = `${sizesToApply.width}px`;
    this.content.nativeElement.style.height = `${sizesToApply.height}px`;

    // Center the content element in the available space.
    const widthMarginSizes = (availableWidth - sizesToApply.width) / 2;
    this.content.nativeElement.style.marginLeft = `${widthMarginSizes}px`;
    this.content.nativeElement.style.marginRight = `${widthMarginSizes}px`;

    // Center the content vertically by applying padding to the top and left.
    const heightMarginSizes = (availableHeight - sizesToApply.height) / 2;
    this.content.nativeElement.style.marginTop = `${heightMarginSizes}px`;
    this.content.nativeElement.style.marginBottom = `${heightMarginSizes}px`;
  }

  public updatePapayaSize() {
    this.studyViewerController.fitPapayaToTheParentElement();
  }
}
